import {
  getDisplayValue,
  parentWithClass,
  parentWithRole,
  attrNameToDisplayName,
} from '@cast/analytics';

export const getSysId = (el: Element): string | undefined =>
  el.getAttribute('data-testid') ?? undefined;

const isHtmlElement = (el: unknown): el is HTMLElement =>
  el instanceof HTMLElement;

export const isDrawerElement = (el: Node): el is HTMLElement =>
  isHtmlElement(el) && el.classList.contains('MuiDrawer-root');

export const isDialogElement = (el: Node): el is HTMLElement =>
  isHtmlElement(el) && el.classList.contains('MuiDialog-root');

export const getSysAttrs = (
  el: Element
): { sysId?: string; sysName?: string } => {
  const sysId = getSysId(el);
  return {
    sysId,
    sysName: sysId ? attrNameToDisplayName(sysId) : undefined,
  };
};

type ParentWithSysIdArgs = {
  sysId: string | RegExp;
  maxLevelUp?: number;
  element: Element;
};

export const parentWithSysId = ({
  sysId,
  maxLevelUp = 10,
  element,
}: ParentWithSysIdArgs): Element | undefined => {
  const predicate =
    typeof sysId === 'string'
      ? (el: Element): boolean => getSysId(el) === sysId
      : (el: Element): RegExpMatchArray | null | undefined =>
          getSysId(el)?.match(sysId);

  let current: Element | null = element;
  let depth = 0;

  while (current && depth < maxLevelUp) {
    if (predicate(current)) {
      return current;
    }
    current = current.parentElement;
    depth++;
  }
  return undefined;
};

type DrawerAttributes = {
  drawerSysId?: string;
  drawerName?: string;
  drawerHeader?: string;
  isSubDrawer?: boolean;
};

export const resolveDrawerAttributes = (
  el: Element
): DrawerAttributes | undefined => {
  const drawerRoot = el.closest('.MuiDrawer-root');
  if (!drawerRoot) {
    return;
  }
  const sysId = getSysId(drawerRoot);
  const header = drawerRoot
    .querySelector('h1, h2, h3, h4, h5, h6')
    ?.textContent?.trim();
  const parent = el.parentElement;
  const allDrawers = parent
    ? Array.from(parent.querySelectorAll(':scope > .MuiDrawer-root'))
    : [];
  const isSubDrawer = allDrawers.length > 0 && allDrawers[0] !== drawerRoot;
  return {
    drawerSysId: sysId,
    drawerName: sysId ? attrNameToDisplayName(sysId) : sysId,
    drawerHeader: header,
    isSubDrawer,
  };
};

type DialogAttributes = {
  dialogSysId?: string;
  dialogName?: string;
  dialogHeader?: string;
};

export const resolveDialogAttributes = (
  el: Element
): DialogAttributes | undefined => {
  const dialogRoot = el.closest('.MuiDialog-root');
  if (!dialogRoot) {
    return;
  }
  const sysId = getSysId(dialogRoot);
  const header = dialogRoot
    .querySelector('h1, h2, h3, h4, h5, h6')
    ?.textContent?.trim();
  return {
    dialogSysId: sysId,
    dialogName: sysId ? attrNameToDisplayName(sysId) : sysId,
    dialogHeader: header,
  };
};

export const isCheckboxChecked = (el: Element): boolean =>
  el.getAttribute('data-indeterminate') === 'true' ||
  !el.classList.contains('Mui-checked');

export const resolveControlEvent = (
  el: Element,
  containerClass: string,
  role: string
): {
  event: Record<string, unknown> | undefined;
  targetEl: Element | undefined;
} => {
  const parent = parentWithRole({ role, element: el });
  let targetEl: Element | undefined;

  if (parent) {
    if (parent.classList.contains(containerClass)) {
      targetEl = parent;
    } else {
      targetEl = parent?.getElementsByClassName(containerClass)[0];
    }
  } else {
    targetEl = parentWithClass({ className: containerClass, element: el });
  }

  if (!targetEl) {
    return { event: undefined, targetEl: undefined };
  }

  return {
    event: {
      ...getSysAttrs(targetEl),
      displayValue: getDisplayValue(
        parentWithClass({
          className: 'MuiFormControlLabel-root',
          element: targetEl,
        }) || targetEl
      ),
    },
    targetEl,
  };
};
